import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FiHelpCircle } from "react-icons/fi";

import styles from "../styles/Navbar.module.css";
import nav_logo from "../assets/images/nav_logo.png";
import bell from "../assets/images/bell.png";
import search from "../assets/images/search.png";
import Log_out from "../assets/images/logout.png";
import arrows from "../assets/images/arrows.png";
import SelectLocationModal from "./SelectLocationModal";
import SwitchModal from "./switchModal";
import AccountLocationSwitcher from "./AccountLocationSwitcher";
import { useQueryClient } from "@tanstack/react-query";
import { performLogout } from "../utils/logout";
import useRoles from "../hooks/useRoles";
import { FaWarehouse, FaChevronDown } from "react-icons/fa";
import { FiLogOut, FiBell } from "react-icons/fi";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import NotificationMenu from "./NotificationMenu";
import { useNotifications } from "../contexts/NotificationContext";
import { canAccessFeature, getUserIdentity } from "../utils/authorization";
import { canAccess, getUserRoleSlug } from "../utils/authorization";
const ALL_NAV_LINKS = [
  { path: "dashboard", label: "Dashboard", page: "dashboard" },
  { path: "my-tasks", label: "My Tasks", page: "my_tasks" },
  { path: "billings", label: "Billing", page: "billing" },
  { path: "billing-settings", label: "Billing Setting", page: "billing_settings" },
  { path: "device-orders", label: "Device Orders", page: "device_orders" },
  { path: "case-manager", label: "Account Insights", page: "account_insights" },
  { path: "user-settings", label: "User Settings", page: "user_settings" },
  { path: "admin/dashboard", label: "Admin", page: "admin" },
  { path: "executive-dashboard", label: "Executive Dashboard", page: "executive_dashboard" },
  // { path: "inventory",          label: "Inventory",          page: "inventory" },
];

const getNavLinksForUser = (user) => {
  if (!user) return [];
  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return [];
  const isTocaSuperAdmin = getUserIdentity(user)?.isSuperAdmin ?? false;
  const isTocaAdmin = getUserIdentity(user)?.isAdmin ?? false;
  const isToca = getUserIdentity(user)?.isToca ?? false;
  const isTocaCeo = user?.is_ceo || false;
  return ALL_NAV_LINKS.filter((link) => {
    if (link.page === "my_tasks" && isTocaSuperAdmin && isTocaAdmin && isToca) return false;
    // if (link.page === "executive_dashboard" && !isTocaCeo) return false;
    return canAccess(user, link.page);
  });
};

const LinksRenderer = React.memo(({ links }) => {
  const [activeLink, setActiveLink] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);

  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);

    // Special handling for /my-tasks/* routes
    if (segments[0] === "my-tasks") {
      setActiveLink("my-tasks");
      return;
    }

    const firstTwo = segments.slice(0, 2).join("/");
    setActiveLink(firstTwo);
  }, [location.pathname]);

  // Bug fix: pages using the shared Table's disableInternalScroll (e.g.
  // Billing) offset their sticky <thead> by --toca-sticky-top-offset, which
  // falls back to a hardcoded 136px (fixed 70px .navbar + an assumed ~66px
  // for this tab bar) wherever nothing else sets it. This bar's actual
  // height isn't fixed - it wraps/reflows with viewport width and which nav
  // links a role sees - so that guess drifted out of sync sometimes,
  // letting the header overlap the table's first row. Measuring this bar's
  // real height and publishing it as the CSS var keeps every consumer
  // (styles.navbar is a fixed, known 70px) correct regardless of how tall
  // the tab row actually renders. Set on :root so a page with its own
  // nearer, more specific override (careManagerDashboard's .patient_page)
  // still wins for that page.
  useLayoutEffect(() => {
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;

    const updateOffset = () => {
      const offset = 70 + wrapperEl.offsetHeight - 1;
      document.documentElement.style.setProperty(
        "--toca-sticky-top-offset",
        `${offset}px`
      );
    };

    updateOffset();
    const resizeObserver = new ResizeObserver(updateOffset);
    resizeObserver.observe(wrapperEl);
    return () => resizeObserver.disconnect();
  }, [links]);

  return (
    <div
      ref={wrapperRef}
      className={styles.navbar_wrapper}
      style={{ justifyContent: "flex-end" }}
    >
      <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
        <ul className={styles.navitems}>
          {links.map(({ path, label }) => {
            const isActive = activeLink === path;

            return (
              <li key={path} className={styles.navitem_li}>
                <div className={styles.navitem_inner}>
                  <Link
                    to={`/${path}`}
                    className={`${styles.navitem} ${isActive ? styles.active : ""}`}
                    onClick={() => setActiveLink(path)}
                  >
                    {label}
                  </Link>

                  {isActive && (
                    <button
                      type="button"
                      aria-label={`Help for ${label}`}
                      className={styles.helpIconInline}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(
                          `/help-documentation?section=${encodeURIComponent(path)}`
                        );
                      }}
                      title={`Help: ${label}`}
                    >
                      <FiHelpCircle size={16} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div
        style={{
          height: "20px",
          paddingRight: "3%",
          borderLeft: "2px solid #9697a8",
          opacity: "0.5",
        }}
      ></div>
      <a
        href="/help-documentation"
        aria-label="Help"
        className={styles.helpIconInline2}
        onClick={(e) => {
          e.stopPropagation();
          navigate("/help-documentation");
        }}
        title="Help"
      >
        Help
      </a>
    </div>
  );
});

const showInventory = false;

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [currentLocation, setCurrentLocation] = useState(
    JSON.parse(sessionStorage.getItem("current_loc")) || []
  );
  const [activeLink, setActiveLink] = useState(false);
  const [isSelectlocationOpen, setisSelectlocationOpen] = useState(false);
  const [isSelectOrgModalOpen, setIsSelectOrgModalOpen] = useState(false);

  const locationName = currentLocation?.name || "Location";
  const orgName = sessionStorage.getItem("acc_name") || "Switch Organization";

  const user = useMemo(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  }, [location]);
  const { roles, position } = useRoles();

  const accountRoleNames = (user?.account_roles || []).map((r) => r?.name);

  const isTocaAdmin = roles.includes("TOCA Admin");
  const isInventoryManager = roles.includes("TOCA Inventory Manager");
  const isSuperUser = accountRoleNames.includes("Super User");
  const identity = useMemo(() => getUserIdentity(user) || {}, [user]);
  const isNurse = identity.isNurse;
  const isSuperAdmin = identity.isSuperAdmin;
  const hasCrossAccountAccess = identity.hasCrossAccountAccess;
  const isPatient = identity.isPatient;
  // const isSuperAdmin = getUserIdentity().isSuperAdmin;
  // const hasCrossAccountAccess = getUserIdentity().hasCrossAccountAccess;
  // const isPatient = getUserIdentity().isPatient;
  const isPatientOrCaseManager = roles.some((r) =>
    ["Patient", "Case Manager"].includes(r)
  );

  const isAdminActive = activeLink === "admin";
  const hasExternalRole = Boolean(user?.external_role?.name);
  const isExecutiveDashboard = location.pathname.includes("/executive-dashboard");

  const showOrgSwitcher =  !isExecutiveDashboard;
  const showLocationSwitcher = !isPatientOrCaseManager && !isExecutiveDashboard && !isPatient;
  const showInventoryBtn =
    !isExecutiveDashboard &&
    isAdminActive &&
    (isTocaAdmin || isInventoryManager || isSuperUser);

  const inventoryRoute =
    isTocaAdmin && isSuperUser && !hasExternalRole
      ? "/inventory/device-distribution"
      : isSuperUser && !isTocaAdmin && !isInventoryManager
        ? "/inventory/device-distribution"
        : "/inventory/dashboard";

  useEffect(() => {
    const path = location.pathname.split("/")[1];
    setActiveLink(path.replace("-", "_"));
  }, [location.pathname]);

  const openSelectlocationModal = () => {
    setisSelectlocationOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeSelectlocationModal = () => {
    setisSelectlocationOpen(false);
    document.body.style.overflow = "auto";
  };

  const handleLogout = () => {
    performLogout();
  };

  return (
    <>
      <div className={styles.navbar_spacer}></div>
      <div className={styles.navbar}>
        <img className={styles.nav_logo} src={nav_logo} alt="" />

        <div className={styles.right_menu}>
          {(showLocationSwitcher || (hasCrossAccountAccess && showOrgSwitcher)) && (
            <AccountLocationSwitcher
              showAccountSwitcher={hasCrossAccountAccess && showOrgSwitcher}
              showLocationSwitcher={showLocationSwitcher}
            />
          )}

          {showInventoryBtn && (
            <div className={`${styles.bg_white} ${styles.switch_account}`}>
              <p style={{ fontSize: "11px" }}>Inventory:</p>
              <div
                className={`${styles.bg_white} ${styles.switch_account_inner}`}
                onClick={() => navigate("/inventory/dashboard")}
              >
                <p style={{ fontSize: "13px" }}>Inventory</p>
                <FaWarehouse size={14} />
              </div>
            </div>
          )}

          {isSelectlocationOpen && (
            <SelectLocationModal
              isOpen={isSelectlocationOpen}
              onClose={closeSelectlocationModal}
            />
          )}

          {isSelectOrgModalOpen && (
            <SwitchModal
              isOpen={isSelectOrgModalOpen}
              onClose={() => setIsSelectOrgModalOpen(false)}
            />
          )}

          {isNurse ? <NotificationBell /> : <DecorativeBell />}

          <button
            type="button"
            aria-label="Log out"
            className={styles.logout_icon}
            onClick={handleLogout}
          >
            <FiLogOut size={19} />
          </button>
        </div>
      </div>
    </>
  );
};

/**
 * Decorative bell icon for non-nurse users — matches the May 2026 redesign
 * which shows a notification bell in the header for all users.
 * TH-B5 wires this up to a real notification scope.
 */
const DecorativeBell = () => (
  <div
    className={styles.logout_icon}
    aria-label="Notifications"
    role="presentation"
    style={{ position: "relative" }}
  >
    <FiBell size={19} />
    <span className={styles.notificationBadge}>10</span>
  </div>
);

const NotificationBell = () => {
  const { unreadCount } = useNotifications();

  return (
    <Popover>
      <PopoverTrigger>
        <div className={styles.logout_icon}>
          <FiBell size={19} />
          {unreadCount > 0 && (
            <span className={styles.notificationBadge}>{unreadCount}</span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" closeOnClickOutside={true} disableScroll>
        <NotificationMenu />
      </PopoverContent>
    </Popover>
  );
};

export const NavbarLinks = () => {
  const location = useLocation();
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    setUser(storedUser ? JSON.parse(storedUser) : null);
  }, [location]);

  const links = useMemo(() => getNavLinksForUser(user), [user]);

  return <LinksRenderer links={links} />;
};

export const NAVBAR_LINKS_DATA = ALL_NAV_LINKS;